/**
 * Legacy add-on catalog labels that now live in GET `/assets/facilities`.
 * Never send add-on ids as `facilities` — only ids returned by the facilities catalog.
 */

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === 'object' && !Array.isArray(v);
}

const NAME_KEYS = ['name', 'addon_name', 'facility_name', 'title', 'label', 'description'] as const;

export const PROMOTED_ADDON_FACILITY_NAMES = [
  'lockers',
  'snacks and beverages',
  'private storage',
  'dedicated it support',
  'soundproofing',
  'shower facilities',
] as const;

function normalizeCatalogName(text: string): string {
  return text
    .trim()
    .toLowerCase()
    .replace(/[''`]/g, '')
    .replace(/\s+/g, ' ')
    .replace(/[_-]+/g, ' ');
}

export function catalogRowDisplayName(row: Record<string, unknown>): string {
  for (const k of NAME_KEYS) {
    const v = row[k];
    if (typeof v === 'string' && v.trim()) {
      return v.trim();
    }
  }
  return '';
}

export function isPromotedAddonName(name: string): boolean {
  const key = normalizeCatalogName(name);
  if (!key) {
    return false;
  }
  return PROMOTED_ADDON_FACILITY_NAMES.some(
    (promoted) => key === promoted || key.includes(promoted) || promoted.includes(key),
  );
}

export function isPromotedAddonCatalogRow(row: Record<string, unknown>): boolean {
  return isPromotedAddonName(catalogRowDisplayName(row));
}

export function facilityCatalogRowId(row: Record<string, unknown>): number | null {
  const raw = row.facility_id ?? row.id;
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return raw;
  }
  if (typeof raw === 'string' && raw.trim()) {
    const n = Number(raw.trim());
    if (Number.isFinite(n)) {
      return n;
    }
  }
  return null;
}

function facilityIdByNormalizedName(
  facilityCatalog: Record<string, unknown>[],
): Map<string, number> {
  const out = new Map<string, number>();
  for (const row of facilityCatalog) {
    const name = normalizeCatalogName(catalogRowDisplayName(row));
    const id = facilityCatalogRowId(row);
    if (name && id !== null) {
      out.set(name, id);
    }
  }
  return out;
}

export function facilityIdsInCatalog(facilityCatalog: Record<string, unknown>[]): Set<number> {
  const out = new Set<number>();
  for (const row of facilityCatalog) {
    const id = facilityCatalogRowId(row);
    if (id !== null) {
      out.add(id);
    }
  }
  return out;
}

/** Match a promoted label to a row from GET `/assets/facilities` (never add-on ids). */
export function resolveFacilityIdByName(
  label: string,
  facilityCatalog: Record<string, unknown>[],
): number | null {
  const key = normalizeCatalogName(label);
  if (!key) {
    return null;
  }
  const byName = facilityIdByNormalizedName(facilityCatalog);
  if (byName.has(key)) {
    return byName.get(key)!;
  }
  for (const promoted of PROMOTED_ADDON_FACILITY_NAMES) {
    if (!key.includes(promoted) && !promoted.includes(key)) {
      continue;
    }
    for (const [catalogName, id] of Array.from(byName.entries())) {
      if (catalogName.includes(promoted) || promoted.includes(catalogName)) {
        return id;
      }
    }
  }
  return null;
}

/**
 * @deprecated Facilities API now includes promoted items (e.g. ids 59–64). Returns `facilityRows` unchanged.
 */
export function mergePromotedAddonsIntoFacilityCatalog(
  facilityRows: Record<string, unknown>[],
  _addonRows: Record<string, unknown>[],
): Record<string, unknown>[] {
  return facilityRows;
}

function parseFacilityIdList(value: unknown): number[] {
  if (!Array.isArray(value)) {
    return [];
  }
  const out: number[] = [];
  const seen = new Set<number>();
  for (const item of value) {
    let id: number | null = null;
    if (typeof item === 'number' && Number.isFinite(item)) {
      id = item;
    } else if (typeof item === 'string' && item.trim()) {
      const n = Number(item.trim());
      id = Number.isFinite(n) ? n : null;
    } else if (isPlainObject(item)) {
      id = facilityCatalogRowId(item);
    }
    if (id !== null && !seen.has(id)) {
      seen.add(id);
      out.push(id);
    }
  }
  return out;
}

function orderedFacilityIds(
  facilityCatalog: Record<string, unknown>[],
  selected: Set<number>,
  validCatalogIds: Set<number>,
): number[] {
  const out: number[] = [];
  const seen = new Set<number>();
  for (const row of facilityCatalog) {
    const id = facilityCatalogRowId(row);
    if (id !== null && selected.has(id) && validCatalogIds.has(id) && !seen.has(id)) {
      out.push(id);
      seen.add(id);
    }
  }
  return out;
}

/**
 * Keep only catalog facility ids, map legacy promoted add-ons by name, clear `addons`.
 */
export function migrateAssetBodyPromotedAddonsToFacilities(
  body: Record<string, unknown>,
  facilityCatalog: Record<string, unknown>[],
): Record<string, unknown> {
  if (facilityCatalog.length === 0) {
    return body;
  }

  const validCatalogIds = facilityIdsInCatalog(facilityCatalog);
  const facilityIds = new Set<number>();

  for (const id of parseFacilityIdList(body.facilities)) {
    if (validCatalogIds.has(id)) {
      facilityIds.add(id);
    }
  }

  const addons = body.addons;
  let addonsChanged = false;
  if (Array.isArray(addons)) {
    for (const item of addons) {
      if (!isPlainObject(item)) {
        addonsChanged = true;
        continue;
      }
      if (!isPromotedAddonCatalogRow(item)) {
        addonsChanged = true;
        continue;
      }
      const fid = resolveFacilityIdByName(catalogRowDisplayName(item), facilityCatalog);
      if (fid !== null && validCatalogIds.has(fid)) {
        facilityIds.add(fid);
      }
      addonsChanged = true;
    }
  }

  const prevFacilities = parseFacilityIdList(body.facilities);
  const facilitiesChanged =
    prevFacilities.length !== facilityIds.size ||
    prevFacilities.some((id) => !facilityIds.has(id));
  const hadAddons = Array.isArray(addons) && addons.length > 0;

  if (!facilitiesChanged && !addonsChanged && !hadAddons) {
    return body;
  }

  return {
    ...body,
    facilities: orderedFacilityIds(facilityCatalog, facilityIds, validCatalogIds),
    addons: [],
  };
}

/** Facilities for display: asset facilities plus promoted legacy add-on rows (labels only). */
export function mergeFacilitiesAndPromotedAddonsForDisplay(
  facilities: unknown[] | undefined,
  addons: unknown[] | undefined,
): Record<string, unknown>[] {
  const out: Record<string, unknown>[] = [];
  if (Array.isArray(facilities)) {
    for (const f of facilities) {
      if (isPlainObject(f)) {
        out.push({ ...f });
      } else if (typeof f === 'number' || typeof f === 'string') {
        out.push({ facility_id: f, id: f });
      }
    }
  }
  const names = new Set(
    out.map((row) => normalizeCatalogName(catalogRowDisplayName(row))).filter(Boolean),
  );
  if (Array.isArray(addons)) {
    for (const a of addons) {
      if (!isPlainObject(a) || !isPromotedAddonCatalogRow(a)) {
        continue;
      }
      const name = normalizeCatalogName(catalogRowDisplayName(a));
      if (name && names.has(name)) {
        continue;
      }
      const label = catalogRowDisplayName(a);
      out.push({
        ...a,
        facility_name: typeof a.facility_name === 'string' ? a.facility_name : label,
        name: typeof a.name === 'string' ? a.name : label,
      });
      if (name) {
        names.add(name);
      }
    }
  }
  return out;
}

/** Fill missing facility labels from GET `/assets/facilities` when payloads only carry ids. */
export function enrichFacilitiesWithCatalog(
  facilities: Record<string, unknown>[],
  facilityCatalog: Record<string, unknown>[],
): Record<string, unknown>[] {
  if (facilityCatalog.length === 0) {
    return facilities;
  }
  const byId = new Map<number, Record<string, unknown>>();
  for (const row of facilityCatalog) {
    const id = facilityCatalogRowId(row);
    if (id !== null) {
      byId.set(id, row);
    }
  }
  return facilities.map((facilityRow) => {
    if (catalogRowDisplayName(facilityRow)) {
      return facilityRow;
    }
    const id = facilityCatalogRowId(facilityRow);
    if (id === null || !byId.has(id)) {
      return facilityRow;
    }
    const catalogRow = byId.get(id)!;
    const label = catalogRowDisplayName(catalogRow);
    return {
      ...catalogRow,
      ...facilityRow,
      facility_id: id,
      id,
      facility_name: typeof facilityRow.facility_name === 'string' ? facilityRow.facility_name : label,
      name: typeof facilityRow.name === 'string' ? facilityRow.name : label,
    };
  });
}
